import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import HomePage from "./homePage";
import { AuthProvider } from "./authContext";
import { FeedbackProvider } from "./feedbackContext";
import CookieNotice from "./CookieNotice";
import PrivateRoute from "./privateRoute";
import { I18nProvider, useI18n } from "./utils/i18n";
import { PrivacyPage, TermsPage } from "./legalPages";

const BRAND = "JolyMei";

// Each module loads only when its route is opened, so the home page pays for
// neither, and Grimoire and Sigil (with its Markdown renderer) not for each other.
const PromptWiki = lazy(() => import("./promptWiki"));
const Sigil = lazy(() => import("./Sigil"));

// Shown while a module's code downloads.
const moduleFallback = <div className="min-h-screen bg-[#01040b]" />;

// Keeps the browser tab title in sync with the active route (and language for
// the legal pages). Without this the static index.html title applied to every
// page, mislabeling Sigil and the legal pages as the Grimoire tool.
function RouteTitle() {
  const location = useLocation();
  const { t, language } = useI18n();
  React.useEffect(() => {
    const path = location.pathname;
    let title = BRAND;
    if (path.startsWith("/grimoire")) title = `Grimoire · ${BRAND}`;
    else if (path.startsWith("/sigil")) title = `Sigil · ${BRAND}`;
    else if (path.startsWith("/privacy")) title = `${t("legal.privacy_title")} · ${BRAND}`;
    else if (path.startsWith("/terms")) title = `${t("legal.terms_title")} · ${BRAND}`;
    else if (path.startsWith("/reset-password")) title = `${t("auth.reset.title")} · ${BRAND}`;
    else if (path.startsWith("/verify-email")) title = `${t("auth.verify.title")} · ${BRAND}`;
    document.title = title;
  }, [location.pathname, t, language]);
  return null;
}

function App() {
  return (
    <Router>
      <I18nProvider>
       <FeedbackProvider>
       <AuthProvider>
        <RouteTitle />
        <CookieNotice />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/grimoire"
            element={
              <PrivateRoute>
                <Suspense fallback={moduleFallback}>
                  <PromptWiki />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/sigil"
            element={
              <PrivateRoute>
                <Suspense fallback={moduleFallback}>
                  <Sigil />
                </Suspense>
              </PrivateRoute>
            }
          />
          {/* Emailed links open their result in the home page's auth modal. The
              keys give each route its own HomePage, so arriving from "/" starts fresh. */}
          <Route path="/verify-email" element={<HomePage key="verify-email" initialAuthMode="verify-email" />} />
          <Route path="/reset-password" element={<HomePage key="reset-confirm" initialAuthMode="reset-confirm" />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* Old bookmarks retain a redirect; the chat frontend has been removed. */}
          <Route path="/chat" element={<Navigate replace to="/grimoire" />} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
        </AuthProvider>
        </FeedbackProvider>
      </I18nProvider>
    </Router>
  );
}

export default App;
